import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, resolveMediaUrl } from '../lib/api';
import { useApp } from '../context/AppContext';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { VerifiedBadge } from '../components/app/VerifiedBadge';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import PostCard from '../components/feed/PostCard';
import { Zap, Star, Grid3X3, Camera, Layers, Film, Pencil, RefreshCw, AlertCircle, Send } from 'lucide-react';

function PostTile({ post, onOpen }) {
  const img = post.images?.[0];
  const video = post.videos?.[0];
  return (
    <button
      data-testid="profile-post-tile"
      onClick={() => onOpen(post)}
      className="relative aspect-square bg-card overflow-hidden group"
    >
       {img ? (
        <img src={resolveMediaUrl(img)} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-[1.03]" loading="lazy" />
      ) : video ? (
        <video src={resolveMediaUrl(video)} muted playsInline preload="metadata" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center p-2 bg-secondary/40">
          <p className="text-[11px] text-muted-foreground line-clamp-4 leading-snug">{post.text}</p>
        </div>
      )}
      {post.images?.length > 1 && (
        <Layers size={14} className="absolute top-2 end-2 text-white drop-shadow" />
      )}
    </button>
  );
}

export default function ProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: me, t } = useApp();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [reels, setReels] = useState([]);
  const [tab, setTab] = useState('posts');
  const [openPost, setOpenPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [following, setFollowing] = useState(false);

  const isMe = me?.id === userId;

  const load = useCallback(async () => {
    try {
      setLoading(true); setError('');
      const [pRes, postsRes, reelsRes] = await Promise.allSettled([
        api.get(`/users/${userId}`),
        api.get(`/posts/user/${userId}`),
        api.get(`/reels/user/${userId}`),
      ]);
      if (pRes.status === 'rejected' || postsRes.status === 'rejected') throw new Error('profile');
      setProfile(pRes.value.data);
      setFollowing(Boolean(pRes.value.data.followers?.includes(me?.id)));
      setPosts(Array.isArray(postsRes.value.data) ? postsRes.value.data : postsRes.value.data?.items || []);
      if (reelsRes.status === 'fulfilled') {
        setReels(Array.isArray(reelsRes.value.data) ? reelsRes.value.data : reelsRes.value.data?.items || []);
      } else setReels([]);
    } catch { setError(t('connectionError')); }
    finally { setLoading(false); }
  }, [userId, me?.id, t]);

  useEffect(() => { load(); }, [load]);

  const toggleFollow = async () => {
    try {
      const response = await api.post(`/users/${userId}/follow`);
      setFollowing(response.data.following);
      setProfile((current) => {
        if (!current || !me?.id) return current;
        const followers = Array.isArray(current.followers) ? current.followers : [];
        return {
          ...current,
          followers: response.data.following
            ? [...new Set([...followers, me.id])]
            : followers.filter((id) => id !== me.id),
        };
      });
    } catch {
      setError(t('connectionError'));
    }
  };

  if (loading) return <div className="max-w-[935px] mx-auto p-6 animate-pulse"><div className="h-36 rounded-2xl bg-muted mb-6" /><div className="h-8 w-48 bg-muted rounded mb-3" /><div className="h-64 bg-muted rounded-2xl" /></div>;
  if (error || !profile) return <div className="max-w-md mx-auto py-20 text-center"><AlertCircle className="mx-auto mb-3 text-destructive" /><p className="text-sm text-muted-foreground">{error || t('connectionError')}</p><Button data-testid="profile-retry-button" variant="outline" className="mt-5" onClick={load}><RefreshCw size={15} className="me-2" />{t('retry')}</Button></div>;

  return (
    <div className="max-w-[935px] mx-auto pb-4" data-testid="profile-page">
      {/* ===== Instagram-style header ===== */}
      <div className="flex items-start gap-5 sm:gap-10 px-4 pt-6 pb-4" data-testid="profile-header">
        <Avatar className="w-20 h-20 sm:w-36 sm:h-36 shrink-0 ring-1 ring-border">
          <AvatarImage src={resolveMediaUrl(profile.avatar)} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          <AvatarFallback className="text-2xl">{profile.username?.[0]?.toUpperCase()}</AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          {/* username row + actions */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5 min-w-0">
              <h1 className="text-lg sm:text-xl font-normal truncate" dir="ltr" data-testid="profile-username">{profile.username}</h1>
              {profile.verified && <VerifiedBadge size={18} />}
              {profile.role === 'admin' && <Badge variant="outline" className="text-[10px]">ADMIN</Badge>}
            </div>
            {isMe ? (
              <Button data-testid="profile-edit-button" size="sm" variant="secondary" className="h-8" onClick={() => navigate('/settings')}><Pencil size={14} className="me-1.5" />{t('edit')}</Button>
            ) : (
              <div className="flex gap-2">
                <Button data-testid="profile-follow-button" size="sm" onClick={toggleFollow} variant={following ? 'secondary' : 'default'} className="h-8 px-5">{following ? t('unfollow') : t('follow')}</Button>
                <Button data-testid="profile-message-button" size="sm" variant="secondary" className="h-8" onClick={() => navigate(`/messages/${userId}`)}><Send size={14} className="me-1.5" />{t('messages')}</Button>
              </div>
            )}
          </div>

          {/* counters */}
          <div className="flex gap-6 sm:gap-10 mt-4 text-sm" data-testid="profile-counters">
            <span data-testid="profile-posts-count"><b className="tabular-nums">{posts.length}</b> <span className="text-muted-foreground">{t('posts')}</span></span>
            <span data-testid="profile-followers-count"><b className="tabular-nums">{Array.isArray(profile.followers) ? profile.followers.length : profile.followers || 0}</b> <span className="text-muted-foreground">{t('followers')}</span></span>
            <span data-testid="profile-following-count"><b className="tabular-nums">{Array.isArray(profile.following) ? profile.following.length : profile.following || 0}</b> <span className="text-muted-foreground">{t('following')}</span></span>
          </div>

          {/* name + bio */}
          <div className="mt-3 hidden sm:block">
            <p className="text-sm font-semibold">{profile.full_name}</p>
            {profile.bio && <p className="text-sm text-foreground/90 whitespace-pre-line mt-0.5" data-testid="profile-bio">{profile.bio}</p>}
            <div className="flex gap-3 mt-2">
              <Badge variant="outline" className="gap-1 text-muted-foreground"><Zap size={11} /> {profile.xp} {t('xp')}</Badge>
              <Badge variant="outline" className="gap-1 text-muted-foreground"><Star size={11} /> {profile.stars} {t('stars')}</Badge>
            </div>
          </div>
        </div>
      </div>

      {/* mobile bio */}
      <div className="px-4 sm:hidden">
        <p className="text-sm font-semibold">{profile.full_name}</p>
        {profile.bio && <p className="text-sm text-foreground/90 whitespace-pre-line mt-0.5">{profile.bio}</p>}
        <div className="flex gap-3 mt-2">
          <Badge variant="outline" className="gap-1 text-muted-foreground"><Zap size={11} /> {profile.xp} {t('xp')}</Badge>
          <Badge variant="outline" className="gap-1 text-muted-foreground"><Star size={11} /> {profile.stars} {t('stars')}</Badge>
        </div>
      </div>

      {/* Posts and reels */}
      <div className="mt-6 border-t border-border">
        <div className="flex justify-center gap-8 py-3 text-xs font-semibold tracking-widest text-muted-foreground uppercase">
          <button data-testid="profile-posts-tab" onClick={() => setTab('posts')} className={`flex items-center gap-2 ${tab === 'posts' ? 'text-primary' : ''}`}><Grid3X3 size={14} /> {t('posts')}</button>
          <button data-testid="profile-reels-tab" onClick={() => setTab('reels')} className={`flex items-center gap-2 ${tab === 'reels' ? 'text-primary' : ''}`}><Film size={14} /> {t('reels')}</button>
        </div>
        {tab === 'posts' && posts.length > 0 ? (
          <div className="grid grid-cols-3 gap-[2px]" data-testid="profile-post-grid">
            {posts.map((p) => <PostTile key={p.id} post={p} onOpen={setOpenPost} />)}
          </div>
        ) : tab === 'reels' && reels.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="profile-reels-grid">
            {reels.map((r) => <button data-testid={`profile-reel-${r.id}`} key={r.id} onClick={() => window.open(resolveMediaUrl(r.video_url), '_blank', 'noopener,noreferrer')} className="relative aspect-[4/5] overflow-hidden rounded-lg bg-card border border-border text-start">
              <video src={resolveMediaUrl(r.video_url)} muted playsInline preload="metadata" className="w-full h-full object-cover" />
              <span className="absolute inset-x-2 bottom-2 text-xs text-white line-clamp-2">{r.caption}</span>
            </button>)}
          </div>
        ) : (
          <div className="text-center py-16 text-muted-foreground">
            {tab === 'posts' ? <Camera size={40} className="mx-auto mb-3 opacity-40" strokeWidth={1.4} /> : <Film size={40} className="mx-auto mb-3 opacity-40" strokeWidth={1.4} />}
            <p className="text-sm">{tab === 'posts' ? t('noPostsYet') : t('noReelsYet')}</p>
          </div>
        )}
      </div>

      {/* Post viewer dialog */}
      <Dialog open={!!openPost} onOpenChange={(v) => !v && setOpenPost(null)}>
        <DialogContent className="max-w-lg p-0 gap-0 max-h-[90vh] overflow-y-auto border-border" data-testid="profile-post-dialog" aria-describedby={undefined}>
          <DialogTitle className="sr-only">Post</DialogTitle>
          {openPost && <PostCard post={openPost} onDeleted={(postId) => { setPosts((current) => current.filter((post) => post.id !== postId)); setOpenPost(null); }} />}
        </DialogContent>
      </Dialog>

    </div>
  );
}
